import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, SignedIn, SignedOut, RedirectToSignIn } from "@clerk/clerk-react";
import { apiGet } from "../lib/api.js";

interface CategoryVendor {
  id: string;
  name: string;
  description: string | null;
  city: string | null;
  region: string | null;
  primary_image_id: string | null;
}
interface VendorCategoryGroup {
  category: string;
  label: string;
  count: number;
  vendors: CategoryVendor[];
}

const API_BASE = (): string =>
  (window as unknown as { __BRIDEPATH_API__?: string }).__BRIDEPATH_API__ ?? "";

export function VendorsBrowse() {
  return (
    <>
      <SignedOut><RedirectToSignIn /></SignedOut>
      <SignedIn><VendorsBrowseInner /></SignedIn>
    </>
  );
}

function VendorsBrowseInner() {
  const { getToken } = useAuth();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const stableGetToken = useCallback(() => getTokenRef.current(), []);
  const [groups, setGroups] = useState<VendorCategoryGroup[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      const res = await apiGet<{ categories: VendorCategoryGroup[] }>("/vendors/by-category?per=8", token);
      if (!cancelled) setGroups(res.ok ? res.value.categories : []);
    })();
    return () => { cancelled = true; };
  }, [getToken]);

  return (
    <section className="max-w-6xl mx-auto px-6 py-8">
      <header className="mb-6">
        <h2 className="font-display text-3xl">Vendors by category</h2>
        <p className="mt-1 text-sm text-stone-600">Browse the wedding vendors we've found, grouped by what they do.</p>
      </header>

      {!groups && <div className="text-stone-500">Loading vendors…</div>}
      {groups && groups.length === 0 && <div className="text-stone-500">No vendors yet.</div>}

      <div className="space-y-10">
        {groups?.map((g) => (
          <div key={g.category}>
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="font-display text-2xl">{g.label}</h3>
              <span className="text-xs text-stone-500">{g.count} vendor{g.count === 1 ? "" : "s"}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {g.vendors.map((v) => (
                <Link
                  key={v.id}
                  to={`/vendor/${v.id}`}
                  className="group rounded-lg overflow-hidden border border-stone-200 bg-white hover:shadow-md transition-shadow"
                >
                  {v.primary_image_id ? (
                    <AuthedImage
                      src={`${API_BASE()}/admin/image/${v.primary_image_id}`}
                      alt={v.name}
                      getToken={stableGetToken}
                      className="w-full aspect-[4/3] object-cover"
                      placeholderClassName="w-full aspect-[4/3]"
                    />
                  ) : (
                    <div className="w-full aspect-[4/3] bg-stone-100 flex items-center justify-center text-stone-300 text-xs">no image</div>
                  )}
                  <div className="p-3">
                    <div className="font-display text-base leading-tight truncate group-hover:underline">{v.name}</div>
                    {(v.city || v.region) && (
                      <div className="text-xs text-stone-500 mt-0.5 truncate">{[v.city, v.region].filter(Boolean).join(", ")}</div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * <img> can't carry an Authorization header. Fetch the bytes with a FRESH Clerk
 * token (minted per attempt — a captured token expires ~60s), render via blob.
 */
function AuthedImage({
  src,
  alt,
  getToken,
  className,
  placeholderClassName,
}: {
  src: string;
  alt: string;
  getToken: () => Promise<string | null>;
  className?: string;
  placeholderClassName?: string;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [errStatus, setErrStatus] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    let revoke: string | null = null;
    const delays = [0, 600, 1800];
    (async () => {
      for (let i = 0; i < delays.length; i++) {
        if (cancelled) return;
        if (delays[i]) await new Promise((r) => setTimeout(r, delays[i]));
        const token = await getToken();
        if (cancelled) return;
        if (!token) { setErrStatus(401); continue; }
        const res = await fetch(src, { headers: { authorization: `Bearer ${token}` } });
        if (cancelled) return;
        if (res.ok) {
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          revoke = url;
          setBlobUrl(url);
          setErrStatus(null);
          return;
        }
        setErrStatus(res.status);
      }
    })();
    return () => { cancelled = true; if (revoke) URL.revokeObjectURL(revoke); };
  }, [src, getToken]);

  if (!blobUrl) {
    return (
      <div className={`${placeholderClassName ?? "aspect-[4/3]"} bg-stone-100 flex items-center justify-center text-stone-400 text-xs`}>
        {errStatus ? `image ${errStatus}` : "…"}
      </div>
    );
  }
  return <img src={blobUrl} alt={alt} className={className ?? "w-full aspect-[4/3] object-cover"} />;
}
